import React from "react";
import { ActiveLocation } from "../../hooks/useActiveLocations";

interface LocationPickerProps {
  locations: ActiveLocation[];
  /**
   * The document's own `location_id`.
   *
   * NULL means the document names no location — which everywhere else in
   * ZOUR-65 means "the primary location", because that is where Zoho files an
   * unlocated document. So the control shows the primary as the effective
   * selection rather than an empty box: an existing bill is not missing an
   * answer, it has the default one.
   */
  value: string | null;
  onChange: (locationId: string) => void;
  /**
   * Read-only with a reason, for a note that inherits its parent's location.
   * Zoho does not enforce parent = note, so this is never editable on a note
   * raised against a document — see NoteParentLocationResolver.
   */
  lockedReason?: string | null;
  /**
   * Read-only with NO reason line — the registration as plain text under the
   * label, nothing else.
   *
   * Separate from `lockedReason` because the two locks are not the same
   * statement. A note's lock has something to explain ("it follows its parent"),
   * and the caption is the whole point. A posted manual document's lock does
   * not: the document is posted, everything on the screen is already read-only,
   * and a sentence under one field explaining why that field in particular
   * cannot be edited was noise the user asked to remove (2026-09-16).
   *
   * `lockedReason` still locks on its own, so nothing that passes it has to
   * change.
   */
  locked?: boolean;
  disabled?: boolean;
  /**
   * Shown under the control, with a spinner, while a location change is being
   * re-run — e.g. "Re-running checks for Bengaluru Branch…".
   *
   * The picker disables itself for as long as this is set. Changing the
   * registration re-runs the tax on the server and then reloads the page, and
   * between those two the page looked inert; a second pick during that window
   * would race the reload. Optional: a caller that changes nothing on the
   * server (the notes, the manual forms) passes nothing and the control behaves
   * exactly as it did before.
   */
  busyLabel?: string | null;
}

/**
 * ZOUR-65 phase 2 — which GST registration a document is filed against.
 *
 * ONLY RENDER THIS WHEN `hasMultiple` IS TRUE. A company with one registration
 * must see no new control: there is nothing to choose, and a disabled dropdown
 * reading "Head Office" is a question nobody asked. Every caller guards on the
 * flag rather than this component hiding itself, so the surrounding layout does
 * not have to reserve space for something that will not appear.
 *
 * The GSTIN is always shown beside the name. "Bengaluru Branch" means nothing
 * to a reviewer checking a bill; `29AAECN3779L1ZQ` is the thing that has to
 * match the paper in front of them.
 */
export const LocationPicker: React.FC<LocationPickerProps> = ({
  locations,
  value,
  onChange,
  lockedReason,
  locked: lockedWithoutReason,
  disabled,
  busyLabel,
}) => {
  const locked = !!lockedReason || lockedWithoutReason === true;
  const busy = !!busyLabel;

  // NULL reads as the primary. The placeholder survives only when there is no
  // primary to fall back to — a list that has not loaded, or one with no
  // primary flag at all — where an empty control is the honest answer.
  const primaryId =
    locations.find((location) => location.is_primary)?.zoho_location_id ?? null;
  const effectiveValue = value ?? primaryId;

  const selected =
    locations.find((location) => location.zoho_location_id === effectiveValue) ?? null;

  return (
    <div className="flex w-full flex-col gap-1">
      <span className="text-caption-bold font-caption-bold uppercase text-subtext-color">
        Location
      </span>

      {locked ? (
        <>
          <span className="text-body font-body text-default-font">
            {selected ? describe(selected) : "—"}
          </span>
          {lockedReason && (
            <span className="text-caption font-caption text-subtext-color">
              {lockedReason}
            </span>
          )}
        </>
      ) : (
        <>
          <select
            value={effectiveValue ?? ""}
            disabled={disabled || busy}
            onChange={(event) => onChange(event.target.value)}
            className="w-full rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font focus:border-brand-primary focus:outline-none disabled:bg-neutral-100"
          >
            {effectiveValue === null && (
              <option value="" disabled>
                Select a location
              </option>
            )}
            {locations.map((location) => (
              <option
                key={location.zoho_location_id ?? location.name}
                value={location.zoho_location_id ?? ""}
              >
                {describe(location)}
              </option>
            ))}
          </select>

          {/* The only thing under the control now. The explanatory caption that
              used to live here was removed on the user's instruction
              (2026-09-15): the label, the names and the GSTINs already say what
              this is, and a paragraph under every document's picker was noise on
              a page that is mostly small print. */}
          {busy && (
            <span className="flex items-center gap-1.5 text-caption font-caption text-subtext-color">
              <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              {busyLabel}
            </span>
          )}
        </>
      )}
    </div>
  );
};

/** "Bengaluru Branch · 29AAECN3779L1ZQ (KA)" — name alone is not enough to check against paper. */
const describe = (location: ActiveLocation): string => {
  const parts = [location.name];

  if (location.gstin) {
    parts.push(location.gstin);
  }

  return location.state_code
    ? `${parts.join(" · ")} (${location.state_code})`
    : parts.join(" · ");
};

export default LocationPicker;
